"use client";

import Image from "next/image";
import * as React from "react";

import {
  CHAMPION_PLACEHOLDER_URL,
  championImageFallback,
  getChampionImageUrl,
} from "@/shared/lib/champion-image";
import { cn } from "@/shared/lib/utils";

interface ChampionIconProps {
  /** Имя чемпиона как его отдаёт бэкенд (уже в формате DDragon-id). */
  name: string;
  /**
   * KAN-1048: что прочитает скринридер и покажет браузер вместо картинки.
   *
   * По умолчанию это `name`, то есть DDragon-id. Но там, где есть человеческое
   * имя — локализованное «Ясуо» вместо «Yasuo», — подписью обязано быть оно:
   * `alt` существует для человека, а не для кода. При переводе мест вызова на
   * этот компонент подпись однажды потерялась, и поймали это тесты панели
   * тирмейкера, искавшие чемпиона по alt.
   */
  label?: string;
  /** Сторона квадрата в пикселях. */
  size?: number;
  /**
   * KAN-1048: свой адрес первым в цепочке — для мест, где картинка уже известна
   * из манифеста CDN (панель тирмейкера). Промах по нему не фатален: дальше идут
   * обычные ступени, а не заглушка.
   */
  srcOverride?: string | null;
  className?: string;
  title?: string;
}

/**
 * Портрет чемпиона, который не ломается.
 *
 * Без обработчика ошибки браузер рисует СВОЮ заглушку — синий квадрат с «?»,
 * и на списке участников это выглядит как поломка интерфейса. Причин промаха
 * хватает: холодный кэш после смены патча, обрыв до DDragon, чемпион, которого
 * ещё нет в закэшированной версии. Ни одна из них не должна светиться в лицо
 * пользователю.
 *
 * KAN-1048: ступеней несколько, и порядок между ними важен:
 *
 *   свой адрес → CDN → прокси бэкенда → нейтральная заглушка
 *
 * Раньше предпоследней ступени не было: промах CDN сразу давал серый квадрат.
 * 09.09.2026 это стоило прода — картинок не было ТОЛЬКО на CDN, а прокси всё
 * время отдавал их с кодом 200 (KAN-1028). Рабочая картинка была в одном
 * запросе, а показывали заглушку. Заглушка — последнее средство, а не второе.
 */
export function ChampionIcon({
  name,
  label,
  size = 20,
  srcOverride,
  className,
  title,
}: ChampionIconProps) {
  const [step, setStep] = React.useState(0);

  // Цепочка адресов по убыванию предпочтительности. Дубликаты убираем: лишняя
  // ступень означала бы повторный запрос по тому же URL и лишний промах.
  const chain = React.useMemo(() => {
    const urls = [
      srcOverride ?? null,
      getChampionImageUrl(name),
      championImageFallback("champion", name),
      CHAMPION_PLACEHOLDER_URL,
    ].filter((u): u is string => Boolean(u));
    return Array.from(new Set(urls));
  }, [srcOverride, name]);

  // Список виртуализируется и переиспользует узлы: без сброса чемпион, севший
  // на место сломавшегося, унаследовал бы чужую заглушку.
  React.useEffect(() => {
    setStep(0);
  }, [name, srcOverride]);

  // `?? CHAMPION_PLACEHOLDER_URL` — не заглушение проверки, а запись инварианта:
  // заглушка в цепочке есть всегда, поэтому пустым результат быть не может.
  const src = chain[Math.min(step, chain.length - 1)] ?? CHAMPION_PLACEHOLDER_URL;

  return (
    <Image
      src={src}
      alt={label ?? name}
      width={size}
      height={size}
      sizes={`${size}px`}
      unoptimized
      onError={() => setStep((prev) => Math.min(prev + 1, chain.length - 1))}
      className={cn(
        "flex-none rounded-sm border border-border/60 object-cover",
        className,
      )}
      {...(title ? { title } : {})}
    />
  );
}
